/**
 * UVC Brand Refresh — Survey & Feedback State Management
 * Persistent localStorage storage, validation, summaries, and submission endpoints.
 */

const STORAGE_KEY = 'uvc_brand_feedback_v2';

export const PRIMARY_SITES = [
  { id: 'HPW', name: 'HPW (Hyde Park / Woodlawn)', location: 'Center On Cottage Grove • 6323 S Cottage Grove' },
  { id: 'Ravenswood', name: 'Ravenswood', location: 'Ravenswood Fellowship • 4511 N Hermitage' },
  { id: 'Wicker Park', name: 'Wicker Park', location: 'The Den Theatre • 1331 N Milwaukee' },
  { id: 'River Forest', name: 'River Forest', location: 'River Forest UMC • 7970 Lake St' },
  { id: 'Online', name: 'Online / Citywide', location: 'Digital Gathering & Small Groups' }
];

export const REVIEWER_ROLES = [
  'Staff Member / Pastoral Team',
  'Lay Leader / Church Council',
  'Congregation Member',
  'Regular Attender',
  'Visitor / Friend of UVC'
];

export const COLLATERAL_TYPES = [
  'Street Banners & Sidewalk A-Frames',
  'Sunday Worship Projection & Lyric Slides',
  'Welcome Swag, Merch & Canvas Totes',
  'Social Media & Digital Ad Graphics',
  'Sermon Series Visual Identity Packages',
  'Building & Interior Wayfinding Signage',
  'Printed Bulletin & Neighborhood Mailers'
];

export const TAGLINE_OPTIONS = [
  { id: 't1', text: 'Bold. Inclusive. Relevant.', source: 'Current UVC Masterbrand Anchor' },
  { id: 't2', text: 'Bold Faith. Radical Love. Real Chicago.', source: 'Concept 01 (Bold Activism)' },
  { id: 't3', text: 'A Sanctuary for Seekers, Doubters, and Dreamers.', source: 'Concept 02 (Grounded Sanctuary)' },
  { id: 't4', text: 'Sacred Ground for Honest Seekers.', source: 'Concept 03 (Neo-Liturgical)' },
  { id: 't5', text: 'Unapologetically Queer-Affirming. Anti-Racist. Jesus-Rooted.', source: 'Concept 01 Alternative' },
  { id: 't6', text: 'In a Hurried City, Find Space to Breathe.', source: 'Concept 02 Alternative' }
];

const defaultState = {
  profile: {
    name: '',
    email: '',
    role: '',
    site: ''
  },
  strategyFeedback: {
    taglineChoice: 't1',
    customTagline: '',
    missionResonance: 5,
    positioningScore: 5,
    comments: ''
  },
  conceptsFeedback: {
    'concept-1': {
      boldScore: 0,
      inclusiveScore: 0,
      relevantScore: 0,
      siteFitScore: 0,
      whatResonates: '',
      concernsOrQuestions: '',
      microReactions: {}
    },
    'concept-2': {
      boldScore: 0,
      inclusiveScore: 0,
      relevantScore: 0,
      siteFitScore: 0,
      whatResonates: '',
      concernsOrQuestions: '',
      microReactions: {}
    },
    'concept-3': {
      boldScore: 0,
      inclusiveScore: 0,
      relevantScore: 0,
      siteFitScore: 0,
      whatResonates: '',
      concernsOrQuestions: '',
      microReactions: {}
    }
  },
  ballot: {
    rank1: 'concept-1',
    rank2: 'concept-2',
    rank3: 'concept-3',
    priorityCollateral: [
      'Street Banners & Sidewalk A-Frames',
      'Sunday Worship Projection & Lyric Slides',
      'Social Media & Digital Ad Graphics'
    ],
    adviceForLeadership: '',
    isSubmitted: false,
    submittedAt: null
  },
  config: {
    webhookUrl: ''
  }
};

class FeedbackStore {
  constructor() {
    this.state = this.loadState();
    this.listeners = new Set();
  }

  sanitizeBallotRanks(ballot) {
    const allConcepts = ['concept-1', 'concept-2', 'concept-3'];
    const r1 = ballot?.rank1;
    const r2 = ballot?.rank2;
    const r3 = ballot?.rank3;
    const unique = [];
    [r1, r2, r3].forEach(id => {
      if (allConcepts.includes(id) && !unique.includes(id)) {
        unique.push(id);
      }
    });
    allConcepts.forEach(id => {
      if (!unique.includes(id)) {
        unique.push(id);
      }
    });
    return {
      ...(ballot || {}),
      rank1: unique[0],
      rank2: unique[1],
      rank3: unique[2]
    };
  }

  loadState() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...defaultState,
          ...parsed,
          profile: { ...defaultState.profile, ...(parsed.profile || {}) },
          strategyFeedback: { ...defaultState.strategyFeedback, ...(parsed.strategyFeedback || {}) },
          conceptsFeedback: {
            'concept-1': { ...defaultState.conceptsFeedback['concept-1'], ...(parsed.conceptsFeedback?.['concept-1'] || {}) },
            'concept-2': { ...defaultState.conceptsFeedback['concept-2'], ...(parsed.conceptsFeedback?.['concept-2'] || {}) },
            'concept-3': { ...defaultState.conceptsFeedback['concept-3'], ...(parsed.conceptsFeedback?.['concept-3'] || {}) }
          },
          ballot: this.sanitizeBallotRanks(parsed.ballot || defaultState.ballot),
          config: { ...defaultState.config, ...(parsed.config || {}) }
        };
      }
    } catch (e) {
      console.warn('Could not load feedback state from localStorage', e);
    }
    return JSON.parse(JSON.stringify(defaultState));
  }

  saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch (e) {
      console.warn('Could not save feedback state to localStorage', e);
    }
    this.notify();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify() {
    this.listeners.forEach(fn => {
      try { fn(this.state); } catch (err) { console.error(err); }
    });
  }

  getState() {
    return this.state;
  }

  isProfileValid() {
    const { name, email, role, site } = this.state.profile;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return Boolean(
      name && name.trim().length >= 2 &&
      email && emailRegex.test(email.trim()) &&
      role && role.trim().length > 0 &&
      site && site.trim().length > 0
    );
  }

  updateProfile(profileData) {
    this.state.profile = { ...this.state.profile, ...profileData };
    this.saveState();
  }

  updateStrategyFeedback(data) {
    this.state.strategyFeedback = { ...this.state.strategyFeedback, ...data };
    this.saveState();
  }

  updateConceptScore(conceptId, rubricKey, score) {
    if (!this.state.conceptsFeedback[conceptId]) return;
    this.state.conceptsFeedback[conceptId][rubricKey] = score;
    this.saveState();
  }

  updateConceptText(conceptId, field, text) {
    if (!this.state.conceptsFeedback[conceptId]) return;
    this.state.conceptsFeedback[conceptId][field] = text;
    this.saveState();
  }

  toggleMicroReaction(conceptId, itemId, emoji) {
    if (!this.state.conceptsFeedback[conceptId]) return;
    const current = this.state.conceptsFeedback[conceptId].microReactions || {};
    if (current[itemId] === emoji) {
      delete current[itemId];
    } else {
      current[itemId] = emoji;
    }
    this.state.conceptsFeedback[conceptId].microReactions = { ...current };
    this.saveState();
  }

  updateBallot(data) {
    this.state.ballot = { ...this.state.ballot, ...data };
    this.saveState();
  }

  getRankedConceptIds() {
    const allConcepts = ['concept-1', 'concept-2', 'concept-3'];
    const r1 = this.state.ballot?.rank1;
    const r2 = this.state.ballot?.rank2;
    const r3 = this.state.ballot?.rank3;
    const unique = [];
    [r1, r2, r3].forEach(id => {
      if (allConcepts.includes(id) && !unique.includes(id)) {
        unique.push(id);
      }
    });
    allConcepts.forEach(id => {
      if (!unique.includes(id)) {
        unique.push(id);
      }
    });

    if (this.state.ballot.rank1 !== unique[0] || this.state.ballot.rank2 !== unique[1] || this.state.ballot.rank3 !== unique[2]) {
      this.state.ballot.rank1 = unique[0];
      this.state.ballot.rank2 = unique[1];
      this.state.ballot.rank3 = unique[2];
      this.saveState();
    }

    return unique;
  }

  reorderRank(fromIndex, toIndex) {
    const list = this.getRankedConceptIds();
    if (fromIndex < 0 || fromIndex >= list.length || toIndex < 0 || toIndex >= list.length || fromIndex === toIndex) return;
    const item = list.splice(fromIndex, 1)[0];
    list.splice(toIndex, 0, item);
    this.state.ballot.rank1 = list[0];
    this.state.ballot.rank2 = list[1];
    this.state.ballot.rank3 = list[2];
    this.saveState();
  }

  moveRankUp(index) {
    if (index <= 0) return;
    this.reorderRank(index, index - 1);
  }

  moveRankDown(index) {
    if (index >= 2) return;
    this.reorderRank(index, index + 1);
  }

  togglePriorityCollateral(item) {
    const list = new Set(this.state.ballot.priorityCollateral || []);
    if (list.has(item)) {
      list.delete(item);
    } else {
      list.add(item);
    }
    this.state.ballot.priorityCollateral = Array.from(list);
    this.saveState();
  }

  updateConfig(configData) {
    this.state.config = { ...this.state.config, ...configData };
    this.saveState();
  }

  isConceptReviewed(conceptId) {
    const c = this.state.conceptsFeedback[conceptId];
    if (!c) return false;
    return (c.boldScore > 0 || c.inclusiveScore > 0 || c.relevantScore > 0 || c.siteFitScore > 0 || c.whatResonates.trim().length > 0);
  }

  getProgressStats() {
    const isProfile = this.isProfileValid();
    const isStratReviewed = Boolean(this.state.strategyFeedback.taglineChoice || this.state.strategyFeedback.comments.trim());
    const isC1 = this.isConceptReviewed('concept-1');
    const isC2 = this.isConceptReviewed('concept-2');
    const isC3 = this.isConceptReviewed('concept-3');
    const isBallotSet = Boolean(this.state.ballot.rank1 && this.state.ballot.rank2 && this.state.ballot.rank3);

    const steps = [
      { id: 'profile', name: 'Reviewer Profile', complete: isProfile, required: true },
      { id: 'strategy', name: 'Brand Strategy & Tagline', complete: isStratReviewed, required: false },
      { id: 'concept-1', name: 'Concept 01 (Bold Activism)', complete: isC1, required: false },
      { id: 'concept-2', name: 'Concept 02 (Grounded Sanctuary)', complete: isC2, required: false },
      { id: 'concept-3', name: 'Concept 03 (Neo-Liturgical)', complete: isC3, required: false },
      { id: 'ballot', name: 'Ranked-Choice Ballot', complete: isBallotSet, required: true }
    ];

    const completedCount = steps.filter(s => s.complete).length;
    const totalCount = steps.length;
    const percentage = Math.round((completedCount / totalCount) * 100);

    return {
      steps,
      completedCount,
      totalCount,
      percentage,
      isProfileComplete: isProfile,
      isBallotComplete: isBallotSet,
      canSubmit: isProfile && isBallotSet
    };
  }

  generateMarkdownSummary() {
    const s = this.state;
    const dateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    let md = `# Urban Village Church — Brand Refresh Feedback Submission\n`;
    md += `**Submitted At:** ${s.ballot.submittedAt || dateStr}\n\n`;

    md += `## 👤 Reviewer Profile\n`;
    md += `- **Name:** ${s.profile.name || '(Not provided)'}\n`;
    md += `- **Email:** ${s.profile.email || '(Not provided)'}\n`;
    md += `- **Role:** ${s.profile.role || '(Not specified)'}\n`;
    md += `- **Primary Site:** ${s.profile.site || '(Not specified)'}\n\n`;

    md += `## 🗳️ Final Ranked-Choice Concept Ballot\n`;
    md += `1. **1st Choice (Primary Recommendation):** ${this.getConceptName(s.ballot.rank1)}\n`;
    md += `2. **2nd Choice (Secondary Alternative):** ${this.getConceptName(s.ballot.rank2)}\n`;
    md += `3. **3rd Choice (Least Preferred):** ${this.getConceptName(s.ballot.rank3)}\n\n`;

    md += `### Urgent Collateral Priorities for ${s.profile.site || 'UVC'}:\n`;
    if (s.ballot.priorityCollateral && s.ballot.priorityCollateral.length > 0) {
      s.ballot.priorityCollateral.forEach(item => {
        md += `- [x] ${item}\n`;
      });
    } else {
      md += `*(None selected)*\n`;
    }
    md += `\n`;

    if (s.ballot.adviceForLeadership && s.ballot.adviceForLeadership.trim()) {
      md += `### Parting Advice / Pastoral Thoughts for Leadership:\n`;
      md += `> "${s.ballot.adviceForLeadership.trim()}"\n\n`;
    }

    md += `## 📊 Concept Rubric Evaluations\n\n`;
    ['concept-1', 'concept-2', 'concept-3'].forEach(id => {
      const c = s.conceptsFeedback[id];
      md += `### ${this.getConceptName(id)}\n`;
      md += `- **Bold Score (1-5):** ${c.boldScore ? `${c.boldScore}/5` : 'Not rated'}\n`;
      md += `- **Inclusive Score (1-5):** ${c.inclusiveScore ? `${c.inclusiveScore}/5` : 'Not rated'}\n`;
      md += `- **Relevant Score (1-5):** ${c.relevantScore ? `${c.relevantScore}/5` : 'Not rated'}\n`;
      md += `- **Site Fit Score (1-5 for ${s.profile.site || 'Site'}):** ${c.siteFitScore ? `${c.siteFitScore}/5` : 'Not rated'}\n`;
      if (c.whatResonates) md += `- **What Resonates:** ${c.whatResonates}\n`;
      if (c.concernsOrQuestions) md += `- **Questions / Concerns:** ${c.concernsOrQuestions}\n`;
      md += `\n`;
    });

    md += `## 🧭 Strategy & Tagline Resonance\n`;
    const selectedTag = TAGLINE_OPTIONS.find(t => t.id === s.strategyFeedback.taglineChoice);
    md += `- **Preferred Tagline:** ${selectedTag ? selectedTag.text : (s.strategyFeedback.customTagline || 'None')}\n`;
    if (s.strategyFeedback.customTagline) md += `- **Custom Tagline Idea:** "${s.strategyFeedback.customTagline}"\n`;
    md += `- **Mission Statement Resonance:** ${s.strategyFeedback.missionResonance}/5\n`;
    if (s.strategyFeedback.comments) md += `- **Strategy Notes:** ${s.strategyFeedback.comments}\n`;

    return md;
  }

  generateMailtoLink() {
    const s = this.state;
    const recipient = 'leaders@urbanvillagechurch.org';
    const subject = encodeURIComponent(`[UVC Brand Feedback] ${s.profile.name || 'Member'} (${s.profile.site || 'Site'} - ${s.profile.role || 'Role'})`);
    const body = encodeURIComponent(this.generateMarkdownSummary());
    return `mailto:${recipient}?subject=${subject}&body=${body}`;
  }

  getConceptName(id) {
    if (id === 'concept-1') return 'Concept 01: Bold Urban Activism';
    if (id === 'concept-2') return 'Concept 02: Grounded Sanctuary';
    if (id === 'concept-3') return 'Concept 03: Dynamic Neo-Liturgical';
    return id;
  }

  async submitFeedback() {
    if (!this.isProfileValid()) {
      throw new Error('Please fill in your Name, Email, Role, and Primary Site before submitting.');
    }

    const payload = {
      timestamp: new Date().toISOString(),
      profile: this.state.profile,
      ballot: this.state.ballot,
      strategyFeedback: this.state.strategyFeedback,
      conceptsFeedback: this.state.conceptsFeedback,
      markdownSummary: this.generateMarkdownSummary()
    };

    let serverConfirmed = false;
    let submissionId = null;

    // 1. Send POST request to local server API (/api/ballot)
    try {
      const res = await fetch('/api/ballot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          serverConfirmed = true;
          submissionId = data.submissionId;
        }
      } else {
        console.warn(`Server /api/ballot responded with status ${res.status}`);
      }
    } catch (err) {
      console.warn('Could not reach backend /api/ballot, persisting locally to browser', err);
    }

    // 2. If a custom webhook is also configured, dispatch there as well
    const webhookEndpoint = this.state.config?.webhookUrl;
    if (webhookEndpoint && webhookEndpoint.trim().startsWith('http')) {
      try {
        await fetch(webhookEndpoint.trim(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } catch (err) {
        console.warn('Custom webhook POST failed', err);
      }
    }

    // 3. Mark state as submitted and save to localStorage
    const now = new Date();
    this.state.ballot.isSubmitted = true;
    this.state.ballot.serverConfirmed = serverConfirmed;
    this.state.ballot.submissionId = submissionId || `local_${now.getTime()}`;
    this.state.ballot.submittedAt = now.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
    this.saveState();
    return { ...payload, serverConfirmed, submissionId: this.state.ballot.submissionId };
  }

  resetFeedback() {
    localStorage.removeItem(STORAGE_KEY);
    this.state = JSON.parse(JSON.stringify(defaultState));
    this.notify();
  }
}

export const feedbackStore = new FeedbackStore();
